import { useCallback, useMemo, useRef } from 'react';

import type { BaseEventPayload } from '@atlaskit/editor-common/analytics';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { RovoSkillsInsertMenuConfig } from '@atlassian/rovo-skills/insert-menu-provider/types';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

/**
 * Editor API typed against the analytics plugin. The `aiExperience` plugin
 * used by `onSkillSelected` does not have an exported type, so we extend
 * with an index signature to allow accessing it at runtime via optional chaining.
 */
export type EditorAPI =
	| (PublicPluginAPI<[AnalyticsPlugin]> & Record<string, unknown>)
	| null
	| undefined;

export type UseRovoSkillsConfigParams = {
	/** The Atlassian cloud ID for the current site. */
	cloudId: string;
	/** Whether Rovo AI is enabled for the user. */
	isRovoEnabled: boolean;
	/** The editor API reference, used to call handleSkillSelected and fireAnalyticsEvent. */
	editorAPI: EditorAPI;
	/** Content ID for checking AI content enablement. */
	contentId: string;
	/** Whether to show all available skills. Defaults to `true`. */
	showAllSkills?: boolean;
	/** Explicit list of skill slugs to show. When set, only these skills appear. */
	allowedSkills?: string[];
	/** Custom handler for when a skill is selected. Defaults to the AI plugin's `handleSkillSelected` action. */
	onSkillSelected?: (slashCommand: string, skill: unknown) => void;
	/** Custom analytics handler. Defaults to forwarding via the editor analytics plugin. */
	onAnalyticsEvent?: (payload: Record<string, unknown>) => void;
};

/**
 * Returns a `RovoSkillsInsertMenuConfig` for use in editor insert menus.
 * Handles gating, cloudId, and wires `onSkillSelected` and `onAnalyticsEvent`
 * to the editor API.
 *
 * Usage:
 * ```ts
 * const rovoSkillsConfig = useRovoSkillsConfig({ cloudId, isRovoEnabled, editorAPI, contentId });
 * ```
 */
export function useRovoSkillsConfig({
	cloudId,
	isRovoEnabled,
	editorAPI,
	contentId,
	showAllSkills = true,
	allowedSkills,
	onSkillSelected,
	onAnalyticsEvent,
}: UseRovoSkillsConfigParams): RovoSkillsInsertMenuConfig | undefined {
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	// Keep a ref to the latest editorAPI so callbacks always read the
	// current value at invocation time, regardless of when the config
	// was memoised or cached by upstream consumers.
	const editorAPIRef = useRef<EditorAPI>(editorAPI);
	editorAPIRef.current = editorAPI;

	const defaultOnAnalyticsEvent = useCallback((payload: Record<string, unknown>) => {
		const api = editorAPIRef.current;
		api?.analytics?.actions?.fireAnalyticsEvent<BaseEventPayload, 'customEventType'>(
			payload as BaseEventPayload,
		);
	}, []);

	return useMemo(() => {
		if (
			!isRovoEnabled ||
			!isAIEnabledForContent ||
			!cloudId ||
			!expValEquals('platform_editor_insert_menu_ai', 'isEnabled', true)
		) {
			return undefined;
		}

		const handleSkillSelected =
			onSkillSelected ??
			((slashCommand: string, skill: unknown) => {
				setTimeout(() => {
					const api = editorAPIRef.current as
						| {
								core?: {
									actions: {
										execute: (command: unknown) => void;
									};
								};
								aiExperience?: {
									actions: {
										handleSkillSelected: (slashCommand: string, skill: unknown) => unknown;
									};
								};
						  }
						| null
						| undefined;
					const command = api?.aiExperience?.actions.handleSkillSelected(slashCommand, skill);
					if (command !== undefined) {
						api?.core?.actions.execute(command);
					}
				}, 0);
			});

		return {
			enabled: true,
			cloudId,
			showAllSkills,
			allowedSkills,
			onSkillSelected: handleSkillSelected,
			onAnalyticsEvent: onAnalyticsEvent ?? defaultOnAnalyticsEvent,
		};
	}, [
		isRovoEnabled,
		isAIEnabledForContent,
		cloudId,
		showAllSkills,
		allowedSkills,
		onSkillSelected,
		onAnalyticsEvent,
		defaultOnAnalyticsEvent,
	]);
}
